import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from '@/auth/AuthProvider'
import { AuthGuard, RequirePermission } from '@/auth/guards'
import { AppShell } from '@/components/layout/AppShell'
import { Login } from '@/routes/Login'
import { InviteAccept, ResetPassword } from '@/routes/InviteAccept'
import { Dashboard } from '@/routes/Dashboard'
import { Overview } from '@/routes/Overview'
import { SpaceDetail, SpacesIndex } from '@/routes/Spaces'
import { Styleguide } from '@/routes/Styleguide'
import { UmsetzungPage } from '@/routes/Umsetzung'
import { AdminAudit, AdminUsers } from '@/routes/Admin'
import { SimulationProvider } from '@/sim/SimulationProvider'
import { DashProvider } from '@/components/stage/dash/DashProvider'
import { ParameterPage } from '@/routes/sim/parameter'
import { ChannelsPage } from '@/routes/sim/Channels'
import { SignalsPage } from '@/routes/sim/Signals'
import { AnalysisPage } from '@/routes/sim/Analysis'
import { DrivesPage } from '@/routes/sim/Drives'
import { BalancePage } from '@/routes/sim/Balance'
import { ClearancePage } from '@/routes/sim/Clearance'
import { PartsListPage } from '@/routes/sim/PartsList'
import { PlmPage } from '@/routes/sim/Plm'
import { VersionsPage } from '@/routes/sim/Versions'
import { RecorderPage } from '@/routes/sim/Recorder'
import { MujocoPage } from '@/routes/sim/Mujoco'
import { ChainPage } from '@/routes/sim/Chain'
import {
  ArchitecturePage,
  AssemblyPage,
  ConceptPage,
  DocumentationPage,
  KneePage,
  MarketPage,
  PracticePage,
  SoftwarePage,
} from '@/routes/sim/reference-pages'
import '@/sim/sim.css'

/**
 * Routing.
 *
 * Genau drei Pfade sind ohne Sitzung erreichbar: Anmeldung, Einladung,
 * Passwort-Reset. Alles andere liegt hinter <AuthGuard>, der Styleguide
 * eingeschlossen — ein internes Werkzeug legt sein Design-System nicht offen ins
 * Netz.
 */
export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/auth/reset" element={<ResetPassword />} />

          <Route
            path="/*"
            element={
              <AuthGuard>
                {/*
                  Der Simulatorzustand hängt ÜBER der Shell und nicht in einer
                  Route: die Reiter sind verschiedene Sichten auf dasselbe
                  Ergebnis. Eine Kadenz, die auf der Parameterseite anders steht
                  als auf der Antriebsseite, wäre nicht nur unpraktisch — sie
                  stellte zwei verschiedene Roboter nebeneinander.
                */}
                <SimulationProvider>
                  {/*
                    Der Rahmen des Easter Eggs. Er liegt INNERHALB der
                    Simulation, weil das Spiel deren Zahlen erbt, und AUSSERHALB
                    der Shell, weil sein Auslöser in der Kopfleiste sitzt und
                    seine Welt im Hintergrund-Canvas — zwei Enden desselben
                    Baums.
                  */}
                  <DashProvider>
                  <AppShell>
                    <Routes>
                      {/* Die Übersicht zeigt die MASCHINE in Zahlen. Was zum
                          Konto gehört — Bereiche, Rechte — steht unter /konto. */}
                      <Route index element={<Overview />} />
                      <Route path="konto" element={<Dashboard />} />
                      <Route path="spaces" element={<SpacesIndex />} />
                      <Route path="spaces/:slug" element={<SpaceDetail />} />

                      {/*
                      Die Reiter des Simulators. Sie stehen NICHT hinter einem
                      RequirePermission-Guard: der Simulator rechnet aus Daten,
                      die im Repository liegen, und liest nichts aus der
                      Datenbank. Was aus der Datenbank kommt — Bereiche,
                      Personen, Protokoll —, ist weiter unten und dort bewacht.
                    */}
                      {/* Modell, Parameter und Merkmale lagen auf drei Seiten.
                          Die Trennung war eine des Rechenwegs und keine der
                          Bedienung; die alten Pfade führen hierher. */}
                      <Route path="parameter" element={<ParameterPage />} />
                      <Route path="modell" element={<Navigate to="/parameter" replace />} />
                      <Route path="merkmale" element={<Navigate to="/parameter" replace />} />
                      <Route path="simulation" element={<ChannelsPage />} />
                      <Route path="kanaele" element={<SignalsPage />} />
                      <Route path="analyse" element={<AnalysisPage />} />
                      <Route path="antriebe" element={<DrivesPage />} />

                      <Route path="recorder" element={<RecorderPage />} />
                      <Route path="pruefstand" element={<MujocoPage />} />
                      <Route path="kette" element={<ChainPage />} />
                      <Route path="freigang" element={<ClearancePage />} />
                      <Route path="balance" element={<BalancePage />} />
                      <Route path="stueckliste" element={<PartsListPage />} />
                      <Route path="plm" element={<PlmPage />} />
                      <Route path="staende" element={<VersionsPage />} />
                      {/*
                        Umsetzung steht bei den Fachkapiteln und nicht bei den
                        Rechenreitern: sie rechnet zwar, aber nicht die
                        Maschine. Ihre Zahlen sind Wochen, Köpfe und Euro.
                      */}
                      {/*
                        DIE EINZIGE SIMULATORROUTE MIT EINER RECHTEPRÜFUNG.

                        Alle anderen rechnen aus Daten, die ohnehin im Bündel
                        liegen. Die Umsetzung führt Termine, Verantwortung,
                        Personalaufbau und Kosten — Angaben über Menschen und
                        über das Vorhaben, nicht über die Maschine.

                        Der Guard ist die Ergänzung zur ausgeblendeten Kachel:
                        wer den Pfad eintippt, landet hier und nicht auf der
                        Seite.
                      */}
                      <Route
                        path="umsetzung"
                        element={
                          <RequirePermission permission="roadmap:read">
                            <UmsetzungPage />
                          </RequirePermission>
                        }
                      />
                      <Route path="p00" element={<KneePage />} />
                      <Route path="dokumentation" element={<DocumentationPage />} />
                      <Route path="aufbau" element={<AssemblyPage />} />
                      <Route path="architektur" element={<ArchitecturePage />} />
                      <Route path="konzept" element={<ConceptPage />} />
                      <Route path="software" element={<SoftwarePage />} />
                      <Route path="markt" element={<MarketPage />} />
                      <Route path="praxis" element={<PracticePage />} />

                      <Route path="styleguide" element={<Styleguide />} />

                      <Route
                        path="admin/users"
                        element={
                          <RequirePermission permission="user:read">
                            <AdminUsers />
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="admin/audit"
                        element={
                          <RequirePermission permission="audit:read">
                            <AdminAudit />
                          </RequirePermission>
                        }
                      />

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </AppShell>
                  </DashProvider>
                </SimulationProvider>
              </AuthGuard>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
